import { type CSSVariablesResolver, createTheme, type MantineColorsTuple } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { createElement } from 'react';
import classes from './theme.module.css';
import { LAYOUT, TOKENS, type TokenName } from './tokens.js';

/** `primary-600` 6. tonda; diğer tonlar ondan açılıp koyulaşır. */
const brand: MantineColorsTuple = [
  '#E7F1FE',
  '#CFE2FC',
  '#9EC5F9',
  '#6AA6F5',
  '#3F8BF1',
  '#2179EE',
  TOKENS['primary-600'],
  '#095CC4',
  '#074F9E',
  '#053E7C',
];

/** `navy-900` en koyu tonda (menü zemini). */
const navy: MantineColorsTuple = [
  '#E9EEF6',
  '#CDD7E8',
  '#A3B4D1',
  '#7790B8',
  '#52709F',
  '#355585',
  '#21406E',
  '#14315D',
  TOKENS['navy-800'],
  TOKENS['navy-900'],
];

/** Ortak boy verilen giriş bileşenleri (Mantine çekirdeği ve `@mantine/dates`). */
const INPUTS = [
  'TextInput',
  'PasswordInput',
  'NumberInput',
  'Textarea',
  'Autocomplete',
  'TagsInput',
  'NativeSelect',
  'DateInput',
  'DatePickerInput',
  'DateTimePicker',
  'TimePicker',
  'TimeInput',
] as const;

const INPUT_DEFAULTS = { size: 'sm', radius: 'sm' } as const;

const CHEVRON = {
  rightSection: createElement(IconChevronDown, { size: 16, 'aria-hidden': true }),
  rightSectionPointerEvents: 'none',
} as const;

/** İki rengin karışımı (`#RRGGBB`): `weight` oranında `a`, kalanı `b`. */
function mix(a: string, b: string, weight: number): string {
  const channel = (hex: string, index: number) =>
    Number.parseInt(hex.slice(1 + index * 2, 3 + index * 2), 16);
  return `#${[0, 1, 2]
    .map((index) =>
      Math.round(channel(a, index) * weight + channel(b, index) * (1 - weight))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')
    .toUpperCase()}`;
}

/**
 * Gri palet lacivert-griden türetilir (M6f G1-22): Mantine'in nötr grisi (#ADB5BD yer tutucu,
 * page-bg üzerinde ≈ 1,9:1) temaya uymuyordu. 3. ton `border` tokenı (tablo ve ayırıcı), 4. ton
 * düğme kenarı, 5. ton giriş yer tutucusu (beyaz ve page-bg üzerinde ≥ 3:1), 7. ton text-secondary.
 */
const gray: MantineColorsTuple = [
  mix(TOKENS['text-secondary'], TOKENS.surface, 0.03),
  mix(TOKENS['text-secondary'], TOKENS.surface, 0.06),
  mix(TOKENS['text-secondary'], TOKENS.surface, 0.1),
  TOKENS.border,
  mix(TOKENS['text-secondary'], TOKENS.surface, 0.22),
  mix(TOKENS['text-secondary'], TOKENS.surface, 0.67),
  mix(TOKENS['text-secondary'], TOKENS.surface, 0.85),
  TOKENS['text-secondary'],
  mix(TOKENS['text-secondary'], TOKENS['navy-900'], 0.5),
  TOKENS['navy-900'],
];

/** Bilgi tonu (`color="info"`): `info-bg` 1., `info-fg` 6. tonda (M6f G1-02). */
const info: MantineColorsTuple = [
  '#EAF2FE',
  TOKENS['info-bg'],
  '#A9CBF8',
  '#7DAEF2',
  '#5592EA',
  '#367BE0',
  TOKENS['info-fg'],
  '#184FAF',
  '#13408E',
  '#0E316D',
];

/**
 * Tehlike tonu (M6f G1d): Mantine'in varsayılan kırmızısı (#FA5252) beyazda ≈ 3,3:1; hata yazısı,
 * alan hatası ve `color="red"` düğme WCAG AA'yı (metinde 4,5:1) karşılamıyordu [WCAG 1.4.3].
 * 6. ton `danger-fg` (beyazda 5,05:1, page-bg'de 4,68:1): `--mantine-color-error`, `c="red"` ve dolu
 * düğme bu tonu kullanır. Açık tonlar beyazla, koyu tonlar navy-900 ile karışır; 9. ton açık ve hafif
 * biçimin yazısı (rozetteki `danger` yazısıyla aynı karışım, 1. ve 2. tonda ≥ 5,3:1).
 */
const red: MantineColorsTuple = [
  mix(TOKENS['danger-fg'], TOKENS.surface, 0.06),
  mix(TOKENS['danger-fg'], TOKENS.surface, 0.12),
  mix(TOKENS['danger-fg'], TOKENS.surface, 0.25),
  mix(TOKENS['danger-fg'], TOKENS.surface, 0.4),
  mix(TOKENS['danger-fg'], TOKENS.surface, 0.58),
  mix(TOKENS['danger-fg'], TOKENS.surface, 0.78),
  TOKENS['danger-fg'],
  mix(TOKENS['danger-fg'], TOKENS['navy-900'], 0.9),
  mix(TOKENS['danger-fg'], TOKENS['navy-900'], 0.8),
  mix(TOKENS['danger-fg'], TOKENS['navy-900'], 0.7),
];

/**
 * Uyarı tonu (`color="warning"`): Mantine'in `orange` açık biçimi (#D9480F yazı, #FFE8CC zemin)
 * 3,6:1'de kalıyordu; turuncu bant üstündeki turuncu düğme okunmuyordu [WCAG 1.4.3]. 1. ton
 * `warning-bg` (açık biçimin zemini), 4. ton `warning-500`; 6. ton dolu ve beyaz biçim (beyazla
 * ≥ 5:1), 9. ton açık biçimin yazısı: uygulamadaki uyarı yazısıyla aynı karışım (`warning-fg`
 * %70 + navy-900; 1. ve 2. tonda ≥ 5:1).
 */
const warning: MantineColorsTuple = [
  mix(TOKENS['warning-bg'], TOKENS.surface, 0.5),
  TOKENS['warning-bg'],
  mix(TOKENS['warning-500'], TOKENS['warning-bg'], 0.35),
  mix(TOKENS['warning-500'], TOKENS.surface, 0.7),
  TOKENS['warning-500'],
  mix(TOKENS['warning-fg'], TOKENS['warning-500'], 0.6),
  mix(TOKENS['warning-fg'], TOKENS['navy-900'], 0.85),
  mix(TOKENS['warning-fg'], TOKENS['navy-900'], 0.8),
  mix(TOKENS['warning-fg'], TOKENS['navy-900'], 0.75),
  mix(TOKENS['warning-fg'], TOKENS['navy-900'], 0.7),
];

export const felixTheme = createTheme({
  // "Hareketi azalt" tercihine uyulur: animasyonlar kapanır (erişilebilirlik)
  respectReducedMotion: true,
  primaryColor: 'brand',
  primaryShade: 6,
  colors: { brand, navy, info, gray, red, warning },
  fontFamily: "'Inter Variable', Inter, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  headings: {
    fontFamily: "'Inter Variable', Inter, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    fontWeight: '700',
  },
  defaultRadius: 'md',
  radius: { sm: `${LAYOUT.radiusButton}px`, md: `${LAYOUT.radiusCard}px` },
  black: TOKENS['navy-900'],
  components: {
    Button: { defaultProps: { radius: 'sm' } },
    // Bütün girişler tek boyda: 36 px, 14 px yazı (M6f G1-03). Giriş, eşleştirme ve parola
    // formları `size="md"`yi açıkça verir.
    ...Object.fromEntries(INPUTS.map((name) => [name, { defaultProps: INPUT_DEFAULTS }])),
    // Tek aşağı ok (⌄); Mantine'in çift yönlü oku (⇕) tasarımda yok
    Select: { defaultProps: { ...INPUT_DEFAULTS, ...CHEVRON } },
    MultiSelect: { defaultProps: { ...INPUT_DEFAULTS, ...CHEVRON } },
    // BGM sayfalaması: çerçevesiz, etkin sayfa açık mavi zeminde (plan §0.6)
    Pagination: {
      defaultProps: { size: 'sm', gap: 6 },
      classNames: { root: classes.pagination, control: classes.paginationControl },
    },
    // Diyaloglar ortada, beyaz kart üzerinde, kalın başlıkla (docs/ekranlar: "Component Details")
    Modal: {
      defaultProps: {
        radius: 'md',
        centered: true,
        overlayProps: { backgroundOpacity: 0.45, blur: 2 },
      },
      styles: {
        content: { background: TOKENS.surface },
        header: { background: TOKENS.surface, paddingBottom: 8 },
        title: { fontWeight: 700, fontSize: '1.125rem', color: TOKENS['navy-900'] },
      },
    },
  },
});

/** Token'lar `--felix-<ad>` CSS değişkenleri olarak; CSS modülleri bunları kullanır. */
export const felixCssVariables: CSSVariablesResolver = () => ({
  variables: Object.fromEntries(
    (Object.keys(TOKENS) as TokenName[]).map((name) => [`--felix-${name}`, TOKENS[name]]),
  ),
  light: {
    '--mantine-color-body': TOKENS['page-bg'],
    '--mantine-color-dimmed': TOKENS['text-secondary'],
  },
  dark: {},
});
